import theme from '@/assets/theme';
import {
  GxPDataOptions,
  GxPExcursionsOptions,
  HometempratureList,
  lastOpenAlertAgingOptions,
} from '@/common/static_data';
import { prependNoSensorOption } from '@/common/utils/noSensorFilter';
import { prependNoTeamOption } from '@/common/utils/noTeamFilter';
import { useCanShowNoTeamFilter } from '@/hooks/useCanShowNoTeamFilter';
import { themeT } from '@/utils/themeText';
import { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '@/redux-toolkit/store';
import useFilterLookups from '@/hooks/useFilterLookups';
import { useToken } from '@/common/helper';
import {
  ListItem,
  UseFilterConfiguratorParams,
} from '../../ControlTower/types';

const PRESETS_KEY = 'download_report_filter_presets_v1';

type LookupsType = ReturnType<typeof useFilterLookups>;

export default function useFilterConfigurator({
  setIsFilterOpen,
  setSecondaryFiltersDDL,
  setSecondaryFiltersSource,
  setIsDatePicker,
  setFilterTitle,
  setOptionsLoading,
  savedFilterKey,
  lists,
  lookups,
  useHourlyAlertAging = false,
}: UseFilterConfiguratorParams & {
  lookups: LookupsType;
  useHourlyAlertAging?: boolean;
}): (filterKey: string) => void {
  const globalLookups = lookups;
  const loadRequestIdRef = useRef(0);
  const { getUserID } = useToken();
  const canShowNoTeamFilter = useCanShowNoTeamFilter();

  // Execution Legs have their own 5-stage status set, distinct from CL
  // statuses (ZD-2366) -- the Progress filter must reflect whichever view
  // the user is currently in, not always the CL list.
  // In AI History and Raw Data Reports ('report'), progress statuses must
  // ALWAYS be Customer Leg (CL) statuses regardless of active view on Control Tower.
  const activeLegTypeRedux = useSelector(
    (s: RootState) => s.shipmentFilter?.activeLegType
  );
  const activeLegType =
    savedFilterKey === 'ai_history' || savedFilterKey === 'report'
      ? 'CL'
      : activeLegTypeRedux;

  const {
    couriersList,
    shipmentStatusDDL,
    shipmentStatusELDDL,
    healthStatusDDL,
    deliveryStatusDDL,
    PODStatusOptions,
    productList,
    sensorsList,
    alertAssignmentOptions,
    projectIdList,
    allTeams,
    locationTypeList,
    shipperRtgList,
    consigneeRtgList,
    savedFiltersSets,
    loaders,
  } = {
    ...globalLookups,
    ...(lists || {}),
  };

  const setBoth = useCallback(
    (list: ListItem[]) => {
      const safe = Array.isArray(list) ? list : [];
      setSecondaryFiltersSource(safe);
      setSecondaryFiltersDDL(safe);
    },
    [setSecondaryFiltersDDL, setSecondaryFiltersSource]
  );

  const setSyncOptions = useCallback(
    (list: ListItem[]) => {
      loadRequestIdRef.current += 1;
      setOptionsLoading?.(false);
      setBoth(list);
    },
    [setBoth, setOptionsLoading]
  );

  const loadAsyncOptions = useCallback(
    (loader?: () => Promise<ListItem[]>) => {
      const requestId = ++loadRequestIdRef.current;
      setOptionsLoading?.(true);
      setBoth([]);

      Promise.resolve(loader?.() ?? Promise.resolve([]))
        .then((list) => {
          if (requestId !== loadRequestIdRef.current) return;
          setBoth(Array.isArray(list) ? list : []);
        })
        .catch(() => {
          if (requestId !== loadRequestIdRef.current) return;
          setBoth([]);
        })
        .finally(() => {
          if (requestId !== loadRequestIdRef.current) return;
          setOptionsLoading?.(false);
        });
    },
    [setBoth, setOptionsLoading]
  );

  interface PresetItem {
    id?: string | number;
    name?: string;
  }

  const loadPresetsAsListItems = useCallback((): ListItem[] => {
    if (Array.isArray(savedFiltersSets) && savedFiltersSets.length) {
      return savedFiltersSets;
    }
    try {
      const userId = getUserID();
      const presetsKey = userId ? `${PRESETS_KEY}_${userId}` : PRESETS_KEY;
      const raw =
        localStorage.getItem(presetsKey) || localStorage.getItem(PRESETS_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map((p: PresetItem) => ({
          id: String(p?.id ?? ''),
          name: String(p?.name ?? ''),
          title: String(p?.name ?? ''),
        }))
        .filter((x: ListItem) => x.id && (x.name || x.title));
    } catch {
      return [];
    }
  }, [savedFiltersSets, getUserID]);

  return useCallback(
    (fb: string) => {
      setIsFilterOpen(false);
      setIsDatePicker(false);

      switch (fb) {
        case 'courier':
          setFilterTitle(themeT('transportProvider', theme.themeType));
          if (couriersList?.length) setSyncOptions(couriersList);
          else loadAsyncOptions(loaders?.couriers);
          break;

        case 'from_location':
          setFilterTitle('Origin address');
          setSyncOptions([]);
          break;

        case 'to_location':
          setFilterTitle('Destination address');
          setSyncOptions([]);
          break;

        case 'status':
          setFilterTitle('Progress');
          if (activeLegType === 'EL') {
            // EL has its own dedicated state — never mixed with CL data
            if (shipmentStatusELDDL?.length) {
              setSyncOptions(
                (shipmentStatusELDDL || []).map((item) => ({
                  ...item,
                  disabled: item.enabled === false,
                }))
              );
            } else {
              loadAsyncOptions(() =>
                (
                  loaders?.shipmentStatus?.(activeLegType) ??
                  Promise.resolve([])
                ).then((list) =>
                  (list || []).map((item) => ({
                    ...item,
                    disabled: item.enabled === false,
                  }))
                )
              );
            }
          } else if (shipmentStatusDDL?.length) {
            setSyncOptions(
              (shipmentStatusDDL || []).map((item) => ({
                ...item,
                disabled: item.enabled === false,
              }))
            );
          } else {
            loadAsyncOptions(() =>
              (
                loaders?.shipmentStatus?.(activeLegType) ?? Promise.resolve([])
              ).then((list) =>
                (list || []).map((item) => ({
                  ...item,
                  disabled: item.enabled === false,
                }))
              )
            );
          }
          break;

        case 'type':
          setFilterTitle('Risk');
          setSyncOptions(healthStatusDDL ?? []);
          break;

        case 'delivery_status':
          setFilterTitle('Timeliness');
          setSyncOptions(deliveryStatusDDL ?? []);
          break;

        case 'product_type':
          setFilterTitle('Product type');
          setSyncOptions([]);
          break;

        case 'product':
          setFilterTitle('Product');
          if (productList?.length) setSyncOptions(productList);
          else loadAsyncOptions(loaders?.products);
          break;

        case 'sensor':
          setFilterTitle('Sensor');
          if (sensorsList?.length) {
            setSyncOptions(prependNoSensorOption(sensorsList));
          } else {
            loadAsyncOptions(() =>
              Promise.resolve(loaders?.sensors?.() ?? Promise.resolve([])).then(
                (list) => prependNoSensorOption(Array.isArray(list) ? list : [])
              )
            );
          }
          break;

        case 'project_id':
          setFilterTitle('Project ID');
          setSyncOptions(projectIdList);
          break;

        case 'team':
          setFilterTitle(themeT('team', theme.themeType));
          if (allTeams?.length) {
            setSyncOptions(prependNoTeamOption(allTeams, canShowNoTeamFilter));
          } else {
            loadAsyncOptions(() =>
              Promise.resolve(loaders?.teams?.() ?? Promise.resolve([])).then(
                (list) =>
                  prependNoTeamOption(
                    Array.isArray(list) ? list : [],
                    canShowNoTeamFilter
                  )
              )
            );
          }
          break;

        case 'scheduled_pickup_date':
        case 'scheduled_delivery_date':
        case 'customer_defined_ship_date':
        case 'created_at':
          setFilterTitle('Date');
          setIsDatePicker(true);
          setSyncOptions([]);
          break;

        case 'alert_types':
          setFilterTitle('Alerts');
          setSyncOptions([]);
          break;

        case 'temperature':
          setFilterTitle('Temperature');
          setSyncOptions(HometempratureList);
          break;

        case 'pod_status':
          setFilterTitle('POD status');
          setSyncOptions(PODStatusOptions ?? []);
          break;

        case 'last_open_alert_aging':
          setFilterTitle('Last open alert aging');
          setSyncOptions(
            useHourlyAlertAging
              ? [
                  { id: 'lt_1h', name: 'Under 1 h' },
                  { id: '1_4h', name: '1 to 4 h' },
                  { id: '4_24h', name: '4 to 24 h' },
                  { id: 'gt_24h', name: 'Over 24 h' },
                ]
              : lastOpenAlertAgingOptions
          );
          break;

        case 'alert_assignment':
          setFilterTitle('Alert assignment');
          if (alertAssignmentOptions?.length)
            setSyncOptions(alertAssignmentOptions);
          else loadAsyncOptions(loaders?.alertAssignment);
          break;

        case 'excursions':
          setFilterTitle('Excursions');
          setSyncOptions(GxPExcursionsOptions);
          break;

        case 'data':
          setFilterTitle('Data');
          setSyncOptions(GxPDataOptions);
          break;

        case 'location_type':
          setFilterTitle('Location type');
          if (locationTypeList?.length) setSyncOptions(locationTypeList);
          else loadAsyncOptions(loaders?.locationTypes);
          break;

        case 'shipper_rtg':
          setFilterTitle('Shipper RTG');
          if (shipperRtgList?.length) setSyncOptions(shipperRtgList);
          else loadAsyncOptions(loaders?.shipperRtg);
          break;

        case 'consignee_rtg':
          setFilterTitle('Consignee RTG');
          if (consigneeRtgList?.length) setSyncOptions(consigneeRtgList);
          else loadAsyncOptions(loaders?.consigneeRtg);
          break;

        case 'origin_state':
          setFilterTitle('Origin state');
          setSyncOptions([]);
          break;

        case 'destination_state':
          setFilterTitle('Destination state');
          setSyncOptions([]);
          break;

        case 'saved_filters': {
          setFilterTitle('Saved filters sets');
          const presetOptions = loadPresetsAsListItems().map((p) => ({
            ...p,
            _isPreset: true,
          }));
          setSyncOptions(presetOptions);
          break;
        }

        default:
          setFilterTitle(
            fb?.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
          );
          setSyncOptions([]);
      }
    },
    [
      setIsFilterOpen,
      useHourlyAlertAging,
      alertAssignmentOptions,
      setIsDatePicker,
      setFilterTitle,
      setSyncOptions,
      loadAsyncOptions,
      couriersList,
      loaders,
      shipmentStatusDDL,
      shipmentStatusELDDL,
      activeLegType,
      healthStatusDDL,
      deliveryStatusDDL,
      productList,
      sensorsList,
      projectIdList,
      allTeams,
      canShowNoTeamFilter,
      PODStatusOptions,
      locationTypeList,
      shipperRtgList,
      consigneeRtgList,
      loadPresetsAsListItems,
    ]
  );
}
